import type { TenantOwnedDatabase } from '@gat-ship/types'
import { inject, readonly, shallowRef } from 'vue'
import {
  tenantOwnedDatabasesKey,
  type TenantOwnedDatabasesService
} from '@gat-ship/types/injection-keys'
import { useTenantUserApiCalls } from './useTenantUserApiCalls'
import { DatabaseAccessUtils } from '../model/database-access-utils'
import { useUser } from '@gat-ship/user'

export function useTenantOwnedDatabases(): TenantOwnedDatabasesService {
  const { getTenantOwnedDatabasesRequest } = useTenantUserApiCalls()
  /**
   * The owned databases for the tenant user.
   */
  const allTenantDatabases = shallowRef<TenantOwnedDatabase[]>([])

  function setDatabases(data: TenantOwnedDatabase[]) {
    allTenantDatabases.value = data
  }

  /**
   * Fetches the database access grants and owned databases for a tenant user.
   * @param tenantUserId - The ID of the tenant user to fetch the data for.
   * @param options - Optional options for the fetch.
   * @param options.forceFetch - Whether to force a fetch of the data. @default false
   * @param options.subscribeToEvents - Whether to subscribe to events. @default true
   * @returns The database access grants and owned databases for the tenant user.
   */
  async function fetchData(options?: {
    forceFetch?: boolean
    subscribeToEvents?: boolean
  }) {
    const promises: Promise<unknown>[] = []
    // Only fetch the owned databases if the data is not already loaded or if the forceFetch option is true.
    // If the tenant gets a new database, this should be handled by the signalR events.
    if (allTenantDatabases.value.length === 0 || options?.forceFetch) {
      promises.push(
        getTenantOwnedDatabasesRequest().then((res) =>
          setDatabases(res.ownedDatabases)
        )
      )
    }
    await Promise.all(promises)
    return {
      databases: readonly(allTenantDatabases.value)
    }
  }

  const user = useUser()

  function getDatabaseAccessItems(userId: string) {
    return DatabaseAccessUtils.selectOwnedDatabasesForUser(
      userId,
      user.id.value,
      allTenantDatabases.value,
      user.databases.value.all
    )
  }
  return {
    allTenantDatabases,
    getDatabaseAccessItems,
    fetchData,
    /**
     * Clears the data and unsubscribes from events.
     * @param options - Optional options for the clear.
     * @param options.unsubscribeFromEvents - Whether to unsubscribe from events. @default true
     */
    clearData: (options?: { unsubscribeFromEvents?: boolean }) => {
      setDatabases([])
      if (options?.unsubscribeFromEvents ?? true) {
        // Reserved: unsubscribe from events when the hook wires up.
      }
    }
  }
}

/** Inject the tenant-owned-databases context provided by a parent provider. */
export function useTenantOwnedDatabasesContext() {
  return inject(tenantOwnedDatabasesKey, undefined)
}
